/* eslint-disable new-parens */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  toastActionReceived,
  toastRegistrationSuccess,
  toastRegistrationError,
  dismissRegistrationToast,
} from "../../../utils/registrationToasts";
import {
  Activity,
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  Brain,
  CalendarDays,
  ChevronRight,
  FileDown,
  Handshake,
  Loader2,
  Mic,
  Palette,
  Sparkles,
  Trophy,
} from "lucide-react";
import {
  BULANDI_CONCLUDED,
  bulandi2026Meta,
  bulandiAdvisors,
  bulandiGoldSponsors,
  bulandiPlatinumSponsors,
  bulandiScheduleDownloadFilename,
  bulandiSchedulePdfUrl,
  bulandiRulebookPdfUrl,
  bulandiRulebookDownloadFilename,
  bulandiTitleSponsor,
} from "../../../data/bulandi2026Data";
import {
  bulandiEventSheetHeader,
  eventRegistrationTierFromBrNumeric,
  fetchBulandiRegistrationTable,
  findMatchingRegistrationRow,
  findSheetColumnKeyForEventName,
  normalizeDobToIso,
  parseBrNumeric,
  postBulandiEventRegistration,
  preselectedEventIdsFromRegistrationRow,
} from "../../../utils/bulandiRegistrationSheet";
import { BULANDI_OFFICIAL_WHATSAPP_GROUP_URL } from "../../../config/whatsappMessaging";
import { buildGroupEventRegistrationFields } from "../../../utils/bulandiEventRegistrationPayload";
import { validateBulandiClosedEventSignInSubmit } from "../../../utils/bulandiEventSignInDeadlines";
import { buildGroupTeammateDefaultsFromRow } from "../../../utils/bulandiEventSheetCell";
import {
  buildViralVoltageRegistrationFields,
  parseViralVoltageInstagramFromSheetCell,
  viralVoltageRegisteredInSheetCell,
} from "../../../utils/bulandiViralVoltageRegistration";
import { BulandiSelectableEventList } from "../../bulandi/BulandiSelectableEventList";
import {
  BrConfirmationModal,
  BulandiRulebookPdfModal,
  BulandiScheduleModal,
  EventRegistrationErrorModal,
  EventRegistrationSuccessModal,
  RulesModal,
} from "../../bulandi/BulandiModals";
import { BulandiContactSection } from "../../bulandi/BulandiContactSection";
import { BulandiRegistrationDrawer } from "../../bulandi/BulandiRegistrationDrawer";
import { BulandiWorkshopsTab } from "../../bulandi/BulandiWorkshopsTab";
import {
  BULANDI_PAGE_TABS,
  BULANDI_VALID_TAB_IDS,
  bulandiIsGroupEventForRegistration,
  eventsFamilyOnly,
  eventsForEventRegistrationByBr,
  eventsIntermediates,
  eventsJuniors,
  eventsSeniorsOnly,
  readBulandiTabFromHash,
} from "../../bulandi/bulandiPageConstants";
import { SponsorPersonCard } from "../../bulandi/SponsorPersonCard";
import ThemeToggle from "../../common/ThemeToggle";
import {
  BULANDI_EVENT_LOGO_URL,
  BUL_HERO_REG_TICKLER_TYPE,
  BUL_EVENTS_SECTION_HEADING_CLASS,
} from "./constants";
import {
  PrimaryCta,
  GhostCta,
  SectionHeader,
  BulandiHeroLiveBanner,
  BulandiClosedNotice,
  BulandiEventsRegisterCta,
} from "./BulandiPageChrome";
import { EventCard } from "./BulandiEventCard";
import {
  eventRegTierLabel,
  eventRegTierTrophyClass,
  eventRegTierChipClass,
  eventRegTierBlurb,
  bulandiEventsSectionWrapClass,
} from "./eventRegistrationLabels";

const Bulandi2026Page = () => {
  const [activeTab, setActiveTab] = useState(readBulandiTabFromHash);
  const eventRegTabPrevRef = useRef(activeTab);
  const pendingEventsScrollTierRef = useRef(null);
  const pendingEventRegPrefillRef = useRef(null);
  const pendingEventRegAutoVerifyRef = useRef(false);
  const [eventsHighlightTier, setEventsHighlightTier] = useState(null);
  const activateBulandiTab = useCallback((tabId, scrollToEventsTier) => {
    if (!BULANDI_VALID_TAB_IDS.has(tabId)) return;
    const wantsEventsScroll =
      tabId === "events" &&
      typeof scrollToEventsTier === "string" &&
      scrollToEventsTier.length > 0;
    pendingEventsScrollTierRef.current = wantsEventsScroll ? scrollToEventsTier : null;
    setActiveTab(tabId);
    if (typeof window !== "undefined") {
      const { pathname, search } = window.location;
      window.history.replaceState(null, "", `${pathname}${search}#${tabId}`);
      if (!wantsEventsScroll) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, []);
  useEffect(() => {
    if (activeTab !== "events") return undefined;
    const tier = pendingEventsScrollTierRef.current;
    if (!tier) return undefined;
    pendingEventsScrollTierRef.current = null;
    const scrollT = setTimeout(() => {
      if (typeof document === "undefined") return;
      const el = document.getElementById(`bulandi-events-${tier}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setEventsHighlightTier(tier);
    }, 80);
    const clearT = setTimeout(() => setEventsHighlightTier(null), 2600);
    return () => {
      clearTimeout(scrollT);
      clearTimeout(clearT);
    };
  }, [activeTab]);
  const [registrationDrawerOpen, setRegistrationDrawerOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [rulebookModalOpen, setRulebookModalOpen] = useState(false);
  const [rulesEvent, setRulesEvent] = useState(null);
  const [brConfirm, setBrConfirm] = useState(null);
  const [eventRegBr, setEventRegBr] = useState("");
  const [eventRegDob, setEventRegDob] = useState("");
  const [eventRegVerifyState, setEventRegVerifyState] = useState("idle");
  const [eventRegError, setEventRegError] = useState("");
  const [eventRegAgeBucket, setEventRegAgeBucket] = useState(null);
  const [eventRegSelectedIds, setEventRegSelectedIds] = useState(() => new Set());
  const [eventRegFromSheetIds, setEventRegFromSheetIds] = useState(() => new Set());
  const [eventRegSuccessModalOpen, setEventRegSuccessModalOpen] = useState(false);
  const [eventRegSubmitError, setEventRegSubmitError] = useState("");
  const [eventRegSubmitting, setEventRegSubmitting] = useState(false);
  const [eventRegGroupTeammates, setEventRegGroupTeammates] = useState({});
  const [eventRegGroupClearBrsByEventId, setEventRegGroupClearBrsByEventId] = useState(
    {},
  );
  const [eventRegViralVoltageInstagramUrl, setEventRegViralVoltageInstagramUrl] =
    useState("");
  const [eventRegViralVoltageInstagramLocked, setEventRegViralVoltageInstagramLocked] =
    useState(false);
  const eventRegVerifiedRef = useRef(false);
  const eventRegSheetCacheRef = useRef({ headers: [], rows: [] });
  const eventRegGroupTeammatesBaselineRef = useRef({});
  useEffect(() => {
    const prev = eventRegTabPrevRef.current;
    eventRegTabPrevRef.current = activeTab;
    const leaving = prev === "event-registration" && activeTab !== "event-registration";
    const entering = prev !== "event-registration" && activeTab === "event-registration";
    if (!leaving && !entering) return;
    eventRegVerifiedRef.current = false;
    setEventRegVerifyState("idle");
    setEventRegError("");
    setEventRegAgeBucket(null);
    setEventRegSelectedIds(() => new Set());
    setEventRegFromSheetIds(() => new Set());
    setEventRegGroupTeammates({});
    setEventRegGroupClearBrsByEventId({});
    eventRegGroupTeammatesBaselineRef.current = {};
    setEventRegViralVoltageInstagramUrl("");
    setEventRegViralVoltageInstagramLocked(false);
    eventRegSheetCacheRef.current = { headers: [], rows: [] };
    setEventRegSuccessModalOpen(false);
    setEventRegSubmitError("");
    setEventRegSubmitting(false);
    if (entering) {
      const prefill = pendingEventRegPrefillRef.current;
      pendingEventRegPrefillRef.current = null;
      if (prefill?.br && prefill?.dob) {
        setEventRegBr(prefill.br);
        setEventRegDob(prefill.dob);
        pendingEventRegAutoVerifyRef.current = true;
        return;
      }
    }
    setEventRegBr("");
    setEventRegDob("");
  }, [activeTab]);
  useEffect(() => {
    if (!eventRegVerifiedRef.current) return;
    eventRegVerifiedRef.current = false;
    setEventRegVerifyState("idle");
    setEventRegAgeBucket(null);
    setEventRegSelectedIds(new Set());
    setEventRegFromSheetIds(new Set());
    setEventRegSuccessModalOpen(false);
    setEventRegSubmitError("");
    setEventRegSubmitting(false);
    setEventRegViralVoltageInstagramUrl("");
    setEventRegViralVoltageInstagramLocked(false);
  }, [eventRegBr, eventRegDob]);
  useEffect(() => {
    const onHashChange = () => setActiveTab(readBulandiTabFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  const handleEventRegVerify = useCallback(async () => {
    setEventRegError("");
    if (BULANDI_CONCLUDED) {
      setEventRegError("Bulandi 2026 has concluded \u2014 event sign-in is closed.");
      setEventRegVerifyState("error");
      return;
    }
    const fetchUrl = (bulandi2026Meta.eventRegistrationSheetFetchUrl || "").trim();
    const sheetId = (bulandi2026Meta.eventRegistrationSpreadsheetId || "").trim();
    if (!fetchUrl && !sheetId) {
      setEventRegError(
        "Event sign-in is not configured: set eventRegistrationSheetFetchUrl (same as registration web app) in bulandi2026Data.js.",
      );
      setEventRegVerifyState("error");
      return;
    }
    const brNumEarly = parseBrNumeric(eventRegBr);
    if (brNumEarly == null) {
      setEventRegError("Enter a valid B No. (e.g. B1511, BR1511, or 1511).");
      setEventRegVerifyState("error");
      return;
    }
    if (!eventRegDob) {
      setEventRegError("Enter your date of birth.");
      setEventRegVerifyState("error");
      return;
    }
    const wantDobEarly = normalizeDobToIso(eventRegDob);
    if (!wantDobEarly || !/^\d{4}-\d{2}-\d{2}$/.test(wantDobEarly)) {
      setEventRegError("Enter a valid date of birth (use the date picker).");
      setEventRegVerifyState("error");
      return;
    }
    if (!eventRegistrationTierFromBrNumeric(brNumEarly)) {
      setEventRegError(
        "B No. must be in a published range: B1001–B1499 (Juniors), B1501–B2999 (Intermediates), or B3001–B10000 (Seniors).",
      );
      setEventRegVerifyState("error");
      return;
    }
    setEventRegVerifyState("loading");
    try {
      const { headers, rows } = await fetchBulandiRegistrationTable(
        sheetId,
        bulandi2026Meta.eventRegistrationSheetGid,
        (bulandi2026Meta.eventRegistrationSheetFetchUrl || "").trim(),
        { skipCache: true },
      );
      eventRegSheetCacheRef.current = { headers, rows };
      const match = findMatchingRegistrationRow(rows, headers, eventRegBr, eventRegDob);
      const tier = eventRegistrationTierFromBrNumeric(brNumEarly);
      const eligible = eventsForEventRegistrationByBr(brNumEarly);
      const preIds = preselectedEventIdsFromRegistrationRow(
        match.row,
        headers,
        eligible,
        match.brNumeric,
      );
      const preIdSet = new Set(preIds);
      const teamDefaults = buildGroupTeammateDefaultsFromRow(
        match.row,
        headers,
        eligible,
        preIdSet,
        match.brNumeric,
        rows,
      );
      setEventRegAgeBucket(tier);
      setEventRegFromSheetIds(preIdSet);
      setEventRegSelectedIds(preIdSet);
      setEventRegGroupTeammates(teamDefaults);
      eventRegGroupTeammatesBaselineRef.current = teamDefaults;
      setEventRegGroupClearBrsByEventId({});
      const viralColKey = findSheetColumnKeyForEventName(headers, "Viral Voltage");
      const viralCell = viralColKey ? match.row[viralColKey] : "";
      const viralLocked = viralVoltageRegisteredInSheetCell(viralCell);
      setEventRegViralVoltageInstagramUrl(parseViralVoltageInstagramFromSheetCell(viralCell));
      setEventRegViralVoltageInstagramLocked(viralLocked);
      setEventRegSuccessModalOpen(false);
      setEventRegSubmitError("");
      eventRegVerifiedRef.current = true;
      setEventRegVerifyState("ok");
    } catch (e) {
      let msg = e?.message || "Could not verify.";
      if (
        e?.name === "TypeError" ||
        /failed to fetch|network|load failed/i.test(String(msg))
      ) {
        msg =
          'Could not reach the Bulandi web app (often CORS). For production: Apps Script \u2192 Deploy \u2192 Web app \u2192 set "Who has access" to Anyone (including anonymous), then New version \u2192 Deploy. Open your /exec URL in a private window \u2014 it must not ask for Google sign-in. Local dev: use npm start (uses a dev-server proxy).';
      }
      setEventRegError(msg);
      setEventRegAgeBucket(null);
      setEventRegSelectedIds(new Set());
      setEventRegFromSheetIds(new Set());
      setEventRegGroupTeammates({});
      eventRegGroupTeammatesBaselineRef.current = {};
      setEventRegGroupClearBrsByEventId({});
      setEventRegViralVoltageInstagramUrl("");
      setEventRegViralVoltageInstagramLocked(false);
      setEventRegSuccessModalOpen(false);
      setEventRegSubmitError("");
      setEventRegVerifyState("error");
    }
  }, [eventRegBr, eventRegDob]);
  useEffect(() => {
    if (!pendingEventRegAutoVerifyRef.current) return;
    if (activeTab !== "event-registration") return;
    if (!eventRegBr.trim() || !eventRegDob) return;
    pendingEventRegAutoVerifyRef.current = false;
    void handleEventRegVerify();
  }, [activeTab, eventRegBr, eventRegDob, handleEventRegVerify]);
  const eventRegEligibleList = useMemo(
    () => eventsForEventRegistrationByBr(parseBrNumeric(eventRegBr.trim())),
    [eventRegBr],
  );
  const eventRegSoloList = useMemo(
    () => eventRegEligibleList.filter((e) => !bulandiIsGroupEventForRegistration(e.id)),
    [eventRegEligibleList],
  );
  const eventRegGroupList = useMemo(
    () => eventRegEligibleList.filter((e) => bulandiIsGroupEventForRegistration(e.id)),
    [eventRegEligibleList],
  );
  const handleEventChoiceSubmit = async () => {
    setEventRegSubmitError("");
    if (BULANDI_CONCLUDED) {
      setEventRegSubmitError(
        "Bulandi 2026 has concluded \u2014 event sign-in is closed.",
      );
      return;
    }
    if (eventRegSelectedIds.size === 0) {
      const m = "Please select at least one competition.";
      setEventRegSubmitError(m);
      toastRegistrationError("event", m);
      return;
    }
    const webAppUrl = (bulandi2026Meta.registrationWebAppUrl || "").trim();
    if (!webAppUrl) {
      const m =
        "Registration web app URL is not configured. Set registrationWebAppUrl in bulandi2026Data.js.";
      setEventRegSubmitError(m);
      toastRegistrationError("event", m);
      return;
    }
    if (!eventRegAgeBucket) return;
    const eligible = eventRegEligibleList;
    const eligibleEventNames = eligible.map((e) => bulandiEventSheetHeader(e));
    const selectedEventNames = eligible
      .filter((e) => eventRegSelectedIds.has(e.id))
      .map((e) => bulandiEventSheetHeader(e));
    const fetchUrl = (bulandi2026Meta.eventRegistrationSheetFetchUrl || "").trim();
    const sheetId = (bulandi2026Meta.eventRegistrationSpreadsheetId || "").trim();
    if (!fetchUrl && !sheetId) {
      const m = "Event sign-in is not configured.";
      setEventRegSubmitError(m);
      toastRegistrationError("event", m);
      return;
    }
    let tid = null;
    try {
      tid = toastActionReceived("Event sign-in");
      setEventRegSubmitting(true);
      setEventRegSuccessModalOpen(false);
      const { rows, headers } = await fetchBulandiRegistrationTable(
        sheetId,
        bulandi2026Meta.eventRegistrationSheetGid,
        fetchUrl,
        { skipCache: true },
      );
      const closedCheck = validateBulandiClosedEventSignInSubmit({
        eligible,
        selectedIds: eventRegSelectedIds,
        fromSheetIds: eventRegFromSheetIds,
        groupTeammateByEventId: eventRegGroupTeammates,
        baselineGroupTeammateByEventId: eventRegGroupTeammatesBaselineRef.current,
        groupClearBrsByEventId: eventRegGroupClearBrsByEventId,
      });
      if (closedCheck.error) {
        dismissRegistrationToast(tid);
        setEventRegSubmitError(closedCheck.error);
        toastRegistrationError("event", closedCheck.error);
        return;
      }
      const grp = buildGroupEventRegistrationFields({
        eligible,
        selectedIds: eventRegSelectedIds,
        fromSheetIds: eventRegFromSheetIds,
        primaryBrRaw: eventRegBr.trim(),
        rows,
        headers,
        groupTeammateByEventId: eventRegGroupTeammates,
        groupClearBrsByEventId: eventRegGroupClearBrsByEventId,
      });
      if (grp.error) {
        dismissRegistrationToast(tid);
        setEventRegSubmitError(grp.error);
        toastRegistrationError("event", grp.error);
        return;
      }
      const viral = buildViralVoltageRegistrationFields({
        selectedIds: eventRegSelectedIds,
        fromSheetIds: eventRegFromSheetIds,
        instagramUrl: eventRegViralVoltageInstagramUrl,
        lockedOnSheet: eventRegViralVoltageInstagramLocked,
      });
      if (viral.error) {
        dismissRegistrationToast(tid);
        setEventRegSubmitError(viral.error);
        toastRegistrationError("event", viral.error);
        return;
      }
      const payload = {
        action: "eventRegistration",
        br: eventRegBr.trim(),
        dob: eventRegDob,
        eligibleEventNames,
        selectedEventNames,
      };
      if (
        grp.groupTeammateCredentialsByEventName &&
        Object.keys(grp.groupTeammateCredentialsByEventName).length
      ) {
        payload.groupTeammateCredentialsByEventName =
          grp.groupTeammateCredentialsByEventName;
      }
      if (
        grp.groupClearTeammateBrsByEventName &&
        Object.keys(grp.groupClearTeammateBrsByEventName).length
      ) {
        payload.groupClearTeammateBrsByEventName = grp.groupClearTeammateBrsByEventName;
      }
      if (viral.viralVoltageInstagramUrl) {
        payload.viralVoltageInstagramUrl = viral.viralVoltageInstagramUrl;
      }
      const regSecret = (bulandi2026Meta.registrationSubmitSecret || "").trim();
      if (regSecret) {
        payload.secret = regSecret;
      }
      const data = await postBulandiEventRegistration(webAppUrl, payload);
      dismissRegistrationToast(tid);
      toastRegistrationSuccess("event", {
        whatsappGroupUrl: BULANDI_OFFICIAL_WHATSAPP_GROUP_URL,
        backgroundSyncNote: data?.eventSheetsSyncScheduled
          ? "Per-event spreadsheets will update in the background within about a minute."
          : "",
      });
      setEventRegSuccessModalOpen(true);
      setEventRegFromSheetIds(new Set(eventRegSelectedIds));
      eventRegGroupTeammatesBaselineRef.current = eventRegGroupTeammates;
      setEventRegGroupClearBrsByEventId({});
      if (viral.viralVoltageInstagramUrl) {
        setEventRegViralVoltageInstagramUrl(viral.viralVoltageInstagramUrl);
      }
      if (eventRegSelectedIds.has("viral-voltage-seniors")) {
        setEventRegViralVoltageInstagramLocked(true);
      }
    } catch (e) {
      dismissRegistrationToast(tid);
      let msg = e?.message || "Could not save your choices.";
      if (
        e?.name === "TypeError" ||
        /failed to fetch|network|load failed/i.test(String(msg))
      ) {
        msg =
          'Could not reach the Bulandi web app. Check deployment ("Anyone" access), URL in bulandi2026Data.js, and use npm start for local dev (proxy).';
      }
      setEventRegSubmitError(msg);
      toastRegistrationError("event", msg);
    } finally {
      setEventRegSubmitting(false);
    }
  };
  const handleEventCardRegister = useCallback(() => {
    activateBulandiTab("event-registration");
  }, [activateBulandiTab]);
  const handleBrConfirmGoToEventRegistration = useCallback(() => {
    if (!brConfirm?.brNumber || !brConfirm?.dob) return;
    pendingEventRegPrefillRef.current = {
      br: brConfirm.brNumber,
      dob: brConfirm.dob,
    };
    setBrConfirm(null);
    activateBulandiTab("event-registration");
  }, [activateBulandiTab, brConfirm]);
  return React.createElement(
    "div",
    {
      className:
        "bulandi-theme min-h-screen text-bul-fg selection:bg-bul-lime/40 selection:text-bul-bg",
    },
    React.createElement("div", {
      "aria-hidden": true,
      className:
        "pointer-events-none absolute inset-x-0 top-0 h-[420px] bulandi-hero-top-glow",
    }),
    React.createElement(
      "div",
      {
        className:
          "relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8 sm:py-10 pb-16",
      },
      React.createElement(
        "div",
        { className: "mb-6 lg:mb-8 flex items-center justify-between gap-3" },
        React.createElement(
          "a",
          {
            href: "/",
            className:
              "inline-flex items-center gap-2 text-bul-lime hover:text-bul-magenta font-mono uppercase tracking-widest text-xs",
          },
          React.createElement(ArrowLeft, { size: 16 }),
          "Back to Home",
        ),
        React.createElement(ThemeToggle, { variant: "bulandi" }),
      ),
      React.createElement(
        "nav",
        {
          className:
            "sticky top-2 z-30 mb-8 lg:mb-10 rounded-lg border-2 border-bul-border bg-bul-bg/80 p-1 shadow-bul-brutal-sm backdrop-blur-xl supports-[backdrop-filter]:bg-bul-bg/60",
          "aria-label": "Bulandi page sections",
        },
        React.createElement(
          "div",
          {
            role: "tablist",
            className: "grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-6",
            "aria-orientation": "horizontal",
          },
          BULANDI_PAGE_TABS.map((tab) => {
            const selected = activeTab === tab.id;
            return React.createElement(
              "button",
              {
                key: tab.id,
                type: "button",
                role: "tab",
                id: `bulandi-tab-${tab.id}`,
                "aria-selected": selected,
                "aria-controls": tab.id,
                tabIndex: 0,
                onClick: () => activateBulandiTab(tab.id),
                className: `min-h-[36px] whitespace-nowrap rounded-md px-2 py-1.5 text-center font-mono text-[11px] sm:text-xs font-bold uppercase tracking-wider leading-none transition focus:outline-none focus-visible:ring-2 focus-visible:ring-bul-lime focus-visible:ring-offset-2 focus-visible:ring-offset-bul-bg ${selected ? "bg-bul-lime text-[rgb(var(--bul-primary-cta-text-rgb))] shadow-bul-brutal-sm border-2 border-[color:var(--bul-strong-outline)]" : "text-bul-mutedfg hover:bg-bul-surface hover:text-bul-fg border-2 border-transparent"}`,
              },
              tab.label,
            );
          }),
        ),
      ),
      activeTab === "home" &&
        React.createElement(
          "section",
          {
            id: "home",
            role: "tabpanel",
            "aria-labelledby": "bulandi-tab-home",
            className: "scroll-mt-6 mb-12 space-y-16 sm:space-y-20",
          },
          React.createElement(
            "div",
            {
              className:
                "grid grid-cols-1 items-start gap-x-10 gap-y-10 lg:grid-cols-2 lg:items-stretch lg:gap-x-14 lg:gap-y-12",
            },
            React.createElement(
              "div",
              {
                className:
                  "order-1 flex w-full min-w-0 flex-col items-center text-center lg:h-full lg:min-h-0",
              },
              React.createElement(
                "p",
                {
                  className:
                    "w-full text-center font-display uppercase leading-none tracking-tight whitespace-nowrap mb-5 sm:mb-6 text-[0.9375rem] sm:text-[1.0546875rem] md:text-[1.125rem]",
                },
                React.createElement(
                  "span",
                  { className: "bul-heading-chrome" },
                  "SMYM,",
                ),
                React.createElement(
                  "span",
                  { className: "bul-text-gradient-fire" },
                  " Chennai",
                ),
              ),
              React.createElement(
                "div",
                {
                  className:
                    "relative w-full max-w-[min(36rem,92%)] lg:flex lg:min-h-0 lg:max-w-none lg:flex-1 lg:flex-col",
                },
                React.createElement(
                  "div",
                  { className: "relative flex w-full justify-center" },
                  React.createElement("div", {
                    "aria-hidden": true,
                    className:
                      "absolute -inset-6 rounded-full bg-gradient-to-tr from-bul-magenta/25 via-bul-violet/15 to-transparent blur-3xl lg:-inset-8",
                  }),
                  React.createElement("img", {
                    src: BULANDI_EVENT_LOGO_URL,
                    alt: "Kanta J Narayan Rathi Bulandi",
                    className:
                      "bulandi-hero-event-logo relative mx-auto block w-full max-w-[min(36rem,92%)] object-contain lg:max-w-[min(100%,27.5rem)] xl:max-w-[min(100%,32.5rem)]",
                    decoding: "async",
                  }),
                ),
                React.createElement(
                  "h2",
                  {
                    className:
                      "-mt-px mb-0 w-full text-center font-display text-[1.125rem] uppercase leading-none tracking-tight text-bul-fg whitespace-nowrap sm:text-[1.265625rem] md:text-[1.40625rem] lg:text-[1.6875rem] xl:text-[1.875rem]",
                  },
                  React.createElement(
                    "span",
                    { className: "bul-heading-chrome" },
                    "Explore your ",
                  ),
                  React.createElement(
                    "span",
                    { className: "bul-text-gradient-fire" },
                    "potential",
                  ),
                ),
                React.createElement(
                  "div",
                  {
                    className:
                      "flex items-center justify-center mt-5 sm:mt-6 lg:mt-0 lg:flex-1 lg:flex-col",
                  },
                  React.createElement(BulandiHeroLiveBanner, null),
                ),
              ),
            ),
            React.createElement(
              "div",
              {
                className:
                  "order-2 col-span-full w-full min-w-0 overflow-hidden rounded-lg border border-bul-border bg-bul-bg/40 py-2.5 sm:py-3 lg:order-3 lg:py-3.5",
              },
              React.createElement(
                "div",
                { className: "flex w-max bulandi-marquee-track" },
                Array.from({ length: 2 }).map((_, idx) =>
                  React.createElement(
                    "div",
                    {
                      key: idx,
                      className: `flex shrink-0 items-center gap-6 whitespace-nowrap pr-6 sm:gap-8 sm:pr-8 md:gap-9 md:pr-9 ${BUL_HERO_REG_TICKLER_TYPE}`,
                    },
                    React.createElement(
                      "span",
                      { className: "text-bul-fg" },
                      "Be Bold",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-magenta", "aria-hidden": true },
                      "\u2726",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-cyan" },
                      "Be Real",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-fg", "aria-hidden": true },
                      "\u2726",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-magenta" },
                      "Be Bulandi",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-cyan", "aria-hidden": true },
                      "\u2726",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-fg" },
                      "One Stage",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-magenta", "aria-hidden": true },
                      "\u2726",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-cyan" },
                      "Endless Possibilities",
                    ),
                    React.createElement(
                      "span",
                      { className: "text-bul-fg", "aria-hidden": true },
                      "\u2726",
                    ),
                  ),
                ),
              ),
            ),
            React.createElement(
              "div",
              {
                className:
                  "order-3 mx-auto flex w-full min-w-0 max-w-xl flex-col lg:order-2 lg:mx-0 lg:h-full lg:max-w-none lg:min-h-0",
              },
              BULANDI_CONCLUDED
                ? React.createElement(BulandiClosedNotice, {
                    className:
                      "lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:justify-center",
                    body: "Registrations, event sign-in and workshop signups are all closed. This page stays up as an archive \u2014 browse the event catalogue with rules and winners, the workshops we ran, and the sponsors who backed the season.",
                  })
                : React.createElement(
                "div",
                {
                  className:
                    "rounded-2xl border-2 border-bul-border bg-bul-surface/90 p-5 shadow-bul-brutal-sm sm:p-6 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col",
                },
                React.createElement(
                  "p",
                  {
                    className:
                      "font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-bul-lime sm:text-xs",
                  },
                  "How to register",
                ),
                React.createElement(
                  "h2",
                  {
                    className:
                      "mt-2 font-display text-[0.9375rem] uppercase leading-tight tracking-tight text-bul-fg sm:text-[1.125rem]",
                  },
                  React.createElement(
                    "span",
                    { className: "bul-heading-chrome" },
                    "Three steps to ",
                  ),
                  React.createElement(
                    "span",
                    { className: "bul-text-gradient-fire" },
                    "your stage",
                  ),
                ),
                React.createElement(
                  "ol",
                  {
                    className: "mt-6 space-y-5 border-t border-bul-border pt-6",
                  },
                  React.createElement(
                    "li",
                    { className: "flex gap-3 sm:gap-4" },
                    React.createElement(
                      "span",
                      {
                        className:
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-bul-border bg-bul-bg font-mono text-xs font-bold text-bul-lime sm:h-9 sm:w-9 sm:text-sm",
                        "aria-hidden": true,
                      },
                      "1",
                    ),
                    React.createElement(
                      "div",
                      { className: "min-w-0 flex-1" },
                      React.createElement(
                        "p",
                        {
                          className:
                            "font-display text-xs uppercase tracking-tight text-bul-fg sm:text-[0.75rem]",
                        },
                        "Get your Bulandi Registration number",
                      ),
                      React.createElement(
                        "button",
                        {
                          type: "button",
                          onClick: () => activateBulandiTab("bulandi-registration"),
                          className:
                            "mt-2 inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-bul-lime underline decoration-bul-lime/40 underline-offset-4 transition hover:text-bul-magenta hover:decoration-bul-magenta/50 sm:text-sm",
                        },
                        "Bulandi registration",
                        React.createElement(ChevronRight, {
                          className: "h-4 w-4 shrink-0",
                          "aria-hidden": true,
                          strokeWidth: 2.5,
                        }),
                      ),
                    ),
                  ),
                  React.createElement(
                    "li",
                    { className: "flex gap-3 sm:gap-4" },
                    React.createElement(
                      "span",
                      {
                        className:
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-bul-border bg-bul-bg font-mono text-xs font-bold text-bul-lime sm:h-9 sm:w-9 sm:text-sm",
                        "aria-hidden": true,
                      },
                      "2",
                    ),
                    React.createElement(
                      "div",
                      { className: "min-w-0 flex-1" },
                      React.createElement(
                        "p",
                        {
                          className:
                            "font-display text-xs uppercase tracking-tight text-bul-fg sm:text-[0.75rem]",
                        },
                        "View event catalogue",
                      ),
                      React.createElement(
                        "button",
                        {
                          type: "button",
                          onClick: () => activateBulandiTab("events"),
                          className:
                            "mt-2 inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-bul-lime underline decoration-bul-lime/40 underline-offset-4 transition hover:text-bul-magenta hover:decoration-bul-magenta/50 sm:text-sm",
                        },
                        "Events",
                        React.createElement(ChevronRight, {
                          className: "h-4 w-4 shrink-0",
                          "aria-hidden": true,
                          strokeWidth: 2.5,
                        }),
                      ),
                    ),
                  ),
                  React.createElement(
                    "li",
                    { className: "flex gap-3 sm:gap-4" },
                    React.createElement(
                      "span",
                      {
                        className:
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-bul-border bg-bul-bg font-mono text-xs font-bold text-bul-lime sm:h-9 sm:w-9 sm:text-sm",
                        "aria-hidden": true,
                      },
                      "3",
                    ),
                    React.createElement(
                      "div",
                      { className: "min-w-0 flex-1" },
                      React.createElement(
                        "p",
                        {
                          className:
                            "font-display text-xs uppercase tracking-tight text-bul-fg sm:text-[0.75rem]",
                        },
                        "Register for events",
                      ),
                      React.createElement(
                        "button",
                        {
                          type: "button",
                          onClick: () => activateBulandiTab("event-registration"),
                          className:
                            "mt-2 inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-bul-lime underline decoration-bul-lime/40 underline-offset-4 transition hover:text-bul-magenta hover:decoration-bul-magenta/50 sm:text-sm",
                        },
                        "Event Sign In",
                        React.createElement(ChevronRight, {
                          className: "h-4 w-4 shrink-0",
                          "aria-hidden": true,
                          strokeWidth: 2.5,
                        }),
                      ),
                    ),
                  ),
                ),
                React.createElement(
                  "div",
                  {
                    className:
                      "mt-6 rounded-xl border border-bul-border bg-bul-bg/50 p-3 sm:mt-7 sm:p-4",
                  },
                  React.createElement(
                    "p",
                    {
                      className:
                        "font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-bul-lime sm:text-xs",
                    },
                    "Good to know",
                  ),
                  React.createElement(
                    "ul",
                    {
                      className:
                        "mt-3 space-y-2.5 text-xs leading-relaxed text-bul-mutedfg sm:text-sm",
                    },
                    React.createElement(
                      "li",
                      { className: "flex gap-2.5" },
                      React.createElement("span", {
                        className:
                          "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-bul-lime shadow-[0_0_8px_rgba(192,242,82,0.45)]",
                        "aria-hidden": true,
                      }),
                      React.createElement(
                        "span",
                        null,
                        React.createElement(
                          "strong",
                          { className: "font-semibold text-bul-fg" },
                          "Only Maheshwari",
                        ),
                        React.createElement(
                          "span",
                          { className: "text-bul-mutedfg" },
                          " \u2014 Bulandi registration is for Maheshwari participants only.",
                        ),
                      ),
                    ),
                    React.createElement(
                      "li",
                      { className: "flex gap-2.5" },
                      React.createElement("span", {
                        className:
                          "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-bul-lime shadow-[0_0_8px_rgba(192,242,82,0.45)]",
                        "aria-hidden": true,
                      }),
                      React.createElement(
                        "span",
                        null,
                        React.createElement(
                          "strong",
                          { className: "font-semibold text-bul-fg" },
                          "One-time registration fee",
                        ),
                        " ",
                        React.createElement(
                          "span",
                          {
                            className: "tabular-nums font-semibold text-bul-lime",
                          },
                          "\u20B9500",
                        ),
                        React.createElement(
                          "span",
                          { className: "text-bul-mutedfg" },
                          " \u2014 paid once for your Bulandi registration number.",
                        ),
                      ),
                    ),
                    React.createElement(
                      "li",
                      { className: "flex gap-2.5" },
                      React.createElement("span", {
                        className:
                          "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-bul-lime shadow-[0_0_8px_rgba(192,242,82,0.45)]",
                        "aria-hidden": true,
                      }),
                      React.createElement(
                        "span",
                        null,
                        React.createElement(
                          "strong",
                          { className: "font-semibold text-bul-fg" },
                          "Age categorization",
                        ),
                        React.createElement(
                          "span",
                          { className: "text-bul-mutedfg" },
                          " is based on date of birth as on ",
                        ),
                        React.createElement(
                          "strong",
                          { className: "font-semibold text-bul-fg" },
                          "1st July 2026",
                        ),
                        React.createElement(
                          "span",
                          { className: "text-bul-mutedfg" },
                          " for Juniors, Intermediates, and Seniors.",
                        ),
                      ),
                    ),
                  ),
                ),
              ),
            ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "div",
              { className: "text-center" },
              React.createElement(
                "h2",
                {
                  className:
                    "font-display text-[1.6875rem] sm:text-[2.25rem] md:text-[2.8125rem] uppercase tracking-tight text-bul-fg",
                },
                React.createElement(
                  "span",
                  { className: "bul-heading-chrome" },
                  "Our ",
                ),
                React.createElement(
                  "span",
                  { className: "bul-text-gradient-fire" },
                  "four pillars",
                ),
              ),
            ),
            React.createElement(
              "div",
              {
                className: "mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6",
              },
              [
                {
                  tag: "Voice",
                  color: "magenta",
                  Icon: Mic,
                  actions: ["Express it", "Own it"],
                },
                {
                  tag: "Mind",
                  color: "cyan",
                  Icon: Brain,
                  actions: ["Think it", "Solve it"],
                },
                {
                  tag: "Creation",
                  color: "amber",
                  Icon: Palette,
                  actions: ["Imagine it", "Create it"],
                },
                {
                  tag: "Motion",
                  color: "lime",
                  Icon: Activity,
                  actions: ["Move it", "Inspire it"],
                },
              ].map((p) => {
                const palette = {
                  magenta: {
                    ring: "border-bul-magenta",
                    tint: "bg-bul-magenta/15",
                    text: "text-bul-magenta",
                  },
                  cyan: {
                    ring: "border-bul-cyan",
                    tint: "bg-bul-cyan/15",
                    text: "text-bul-cyan",
                  },
                  amber: {
                    ring: "border-bul-amber",
                    tint: "bg-bul-amber/15",
                    text: "text-bul-amber",
                  },
                  lime: {
                    ring: "border-bul-lime",
                    tint: "bg-bul-lime/15",
                    text: "text-bul-lime",
                  },
                }[p.color];
                const { Icon } = p;
                return React.createElement(
                  "div",
                  { key: p.tag, className: "text-center" },
                  React.createElement(
                    "div",
                    {
                      className: `mx-auto grid h-20 w-20 place-items-center rounded-full border-2 sm:h-24 sm:w-24 ${palette.ring} ${palette.tint} ${palette.text} bul-glow-magenta`,
                    },
                    React.createElement(Icon, {
                      className: "h-8 w-8 sm:h-10 sm:w-10",
                      "aria-hidden": true,
                      strokeWidth: 2,
                    }),
                  ),
                  React.createElement(
                    "div",
                    {
                      className: `mt-4 font-display text-[0.84375rem] uppercase tracking-tight sm:mt-5 sm:text-[1.125rem] ${palette.text}`,
                    },
                    p.tag,
                  ),
                  p.actions.map((a) =>
                    React.createElement(
                      "div",
                      { key: a, className: "text-sm text-bul-mutedfg" },
                      a,
                    ),
                  ),
                );
              }),
            ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "div",
              { className: "relative mb-10 text-center" },
              React.createElement(
                "h2",
                {
                  className:
                    "font-display text-[1.6875rem] sm:text-[2.25rem] md:text-[2.8125rem] uppercase tracking-tight text-bul-fg",
                },
                React.createElement(
                  "span",
                  { className: "bul-heading-chrome" },
                  "Choose your ",
                ),
                React.createElement(
                  "span",
                  { className: "bul-text-gradient-fire" },
                  "arena",
                ),
              ),
              React.createElement(
                "p",
                {
                  className:
                    "mt-3 mx-auto text-base sm:text-lg text-bul-mutedfg leading-relaxed max-w-md",
                },
                "Find events that match your age group and energy",
              ),
            ),
            React.createElement(
              "div",
              {
                className:
                  "grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5 lg:grid-cols-2 lg:gap-4 xl:grid-cols-4 xl:gap-5",
              },
              [
                {
                  label: "Juniors",
                  tier: "juniors",
                  detail: "Born between 2 Jul 2017 and 1 Jul 2022 (inclusive).",
                  color: "magenta",
                  iconSrc: "/arena/juniors.png",
                },
                {
                  label: "Intermediate",
                  tier: "intermediates",
                  detail: "Born between 2 Jul 2010 and 1 Jul 2017 (inclusive)",
                  color: "cyan",
                  iconSrc: "/arena/intermediate.png",
                },
                {
                  label: "Seniors",
                  tier: "seniors",
                  detail: "Born on or before 1 Jul 2010",
                  color: "amber",
                  iconSrc: "/arena/seniors.png",
                },
                {
                  label: "Family",
                  tier: "family",
                  detail: "Team entry",
                  color: "violet",
                  iconSrc: "/arena/family.png",
                },
              ].map((cat) => {
                const palette = {
                  magenta: {
                    blob: "bg-bul-magenta",
                    tileBg: "bg-bul-magenta/15",
                    tileBorder: "border-bul-magenta/40",
                    tileText: "text-bul-magenta",
                    label: "text-bul-magenta",
                  },
                  cyan: {
                    blob: "bg-bul-cyan",
                    tileBg: "bg-bul-cyan/15",
                    tileBorder: "border-bul-cyan/40",
                    tileText: "text-bul-cyan",
                    label: "text-bul-cyan",
                  },
                  amber: {
                    blob: "bg-bul-amber",
                    tileBg: "bg-bul-amber/15",
                    tileBorder: "border-bul-amber/40",
                    tileText: "text-bul-amber",
                    label: "text-bul-amber",
                  },
                  violet: {
                    blob: "bg-bul-violet",
                    tileBg: "bg-bul-violet/15",
                    tileBorder: "border-bul-violet/40",
                    tileText: "text-bul-violet",
                    label: "text-bul-violet",
                  },
                }[cat.color];
                return React.createElement(
                  "button",
                  {
                    key: cat.label,
                    type: "button",
                    onClick: () => activateBulandiTab("events", cat.tier),
                    className:
                      "group relative flex min-w-0 w-full items-center gap-4 overflow-hidden rounded-2xl border border-bul-border bg-bul-surface p-4 text-left transition hover:-translate-y-1 hover:border-bul-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-bul-lime focus-visible:ring-offset-2 focus-visible:ring-offset-bul-bg sm:gap-6 sm:rounded-3xl sm:p-6 md:gap-7",
                  },
                  React.createElement("div", {
                    "aria-hidden": true,
                    className: `absolute -top-12 -right-12 h-40 w-40 rounded-full opacity-20 blur-2xl ${palette.blob}`,
                  }),
                  React.createElement("img", {
                    src: cat.iconSrc,
                    alt: "",
                    className:
                      "relative z-[1] block h-20 w-20 shrink-0 rounded-3xl object-contain sm:h-24 sm:w-24 xl:h-20 xl:w-20",
                    loading: "lazy",
                    decoding: "async",
                  }),
                  React.createElement(
                    "div",
                    {
                      className:
                        "relative z-[1] min-w-0 flex flex-1 flex-col justify-center gap-1.5",
                    },
                    React.createElement(
                      "div",
                      {
                        className: `whitespace-nowrap font-display text-[0.8125rem] uppercase leading-none tracking-tight sm:text-[0.875rem] xl:text-[0.9375rem] ${palette.label}`,
                      },
                      cat.label,
                    ),
                    React.createElement(
                      "div",
                      {
                        className:
                          "break-words text-left text-[11px] font-normal normal-case leading-snug tracking-normal text-bul-mutedfg sm:text-xs sm:leading-relaxed",
                      },
                      cat.detail,
                    ),
                  ),
                );
              }),
            ),
          ),
          React.createElement(
            "div",
            {
              className:
                "relative overflow-hidden rounded-[2rem] border border-bul-border bg-gradient-to-br from-bul-magenta/30 via-bul-violet/20 to-bul-cyan/20 p-10 text-center md:p-16",
            },
            React.createElement(Sparkles, {
              className: "absolute top-6 right-6 h-8 w-8 text-bul-lime",
              "aria-hidden": true,
              strokeWidth: 2,
            }),
            React.createElement(
              "h2",
              {
                className:
                  "font-display px-1 text-[1.40625rem] uppercase leading-[1.12] tracking-tight text-bul-fg sm:text-[2.25rem] sm:leading-[0.95] md:text-[2.8125rem]",
              },
              React.createElement(
                "span",
                { className: "block sm:inline bul-heading-chrome" },
                BULANDI_CONCLUDED ? "Relive every " : "Your spotlight is ",
              ),
              React.createElement(
                "span",
                { className: "block sm:inline" },
                React.createElement(
                  "span",
                  { className: "bul-text-gradient-hero" },
                  BULANDI_CONCLUDED ? "moment" : "waiting",
                ),
              ),
            ),
            React.createElement(
              "div",
              { className: "mt-8 flex justify-center" },
              React.createElement(
                PrimaryCta,
                {
                  as: "button",
                  type: "button",
                  onClick: () =>
                    activateBulandiTab(
                      BULANDI_CONCLUDED
                        ? "events"
                        : bulandi2026Meta.mainRegistrationOpen === false
                          ? "event-registration"
                          : "bulandi-registration",
                    ),
                  className: "bul-cta-lg",
                },
                BULANDI_CONCLUDED
                  ? "Explore the Events"
                  : bulandi2026Meta.mainRegistrationOpen === false
                    ? "Events Sign In"
                    : "Claim Your Stage",
                React.createElement(ArrowUpRight, {
                  className: "h-5 w-5",
                  "aria-hidden": true,
                  strokeWidth: 2,
                }),
              ),
            ),
          ),
          React.createElement(
            "div",
            { className: "border-t border-bul-border pt-8 text-center" },
            React.createElement(
              "p",
              {
                className:
                  "font-mono text-xs uppercase tracking-widest text-bul-mutedfg",
              },
              "\xA9 2026 SMYM Chennai \xB7 All rights reserved",
            ),
          ),
        ),
      activeTab === "bulandi-registration" &&
        React.createElement(
          "section",
          {
            id: "bulandi-registration",
            role: "tabpanel",
            "aria-labelledby": "bulandi-tab-bulandi-registration",
            className: "scroll-mt-6 mb-12",
          },
          React.createElement(SectionHeader, {
            eyebrow: "Official entry \xB7 Bulandi 2026",
            title: "Claim your",
            accent: "stage.",
            description: bulandi2026Meta.mainRegistrationOpen
              ? "Complete the main Bulandi 2026 registration first \u2014 required for anyone taking part in competitions or activities. After that, head to the Events Sign In tab to lock in your competitions."
              : BULANDI_CONCLUDED
                ? "Bulandi 2026 has concluded \u2014 registrations are closed. Thank you to every participant, volunteer and sponsor who made the season happen."
                : "Main Bulandi registration is closed. If you already have a B No., use the Events Sign In tab to choose or update your competitions.",
            descriptionClassName: "!max-w-none w-full max-w-full",
          }),
          React.createElement(
            "div",
            {
              className:
                "rounded-2xl border-2 border-bul-border bg-bul-surface/90 p-6 sm:p-8 lg:p-10 shadow-bul-brutal-sm",
            },
            React.createElement(
              "div",
              { className: "flex flex-col gap-3 sm:flex-row sm:flex-wrap" },
              React.createElement(
                PrimaryCta,
                {
                  as: "button",
                  type: "button",
                  onClick: () => {
                    if (bulandi2026Meta.mainRegistrationOpen === false) return;
                    setRegistrationDrawerOpen(true);
                  },
                  "aria-disabled":
                    bulandi2026Meta.mainRegistrationOpen === false
                      ? "true"
                      : undefined,
                  tabIndex:
                    bulandi2026Meta.mainRegistrationOpen === false ? -1 : undefined,
                  title:
                    bulandi2026Meta.mainRegistrationOpen === false
                      ? bulandi2026Meta.mainRegistrationClosedLabel ||
                        "Registration closed"
                      : undefined,
                  className: `flex-1 min-w-[min(100%,16rem)]${
                    bulandi2026Meta.mainRegistrationOpen === false
                      ? " cursor-not-allowed opacity-55 saturate-50"
                      : ""
                  }`,
                },
                bulandi2026Meta.mainRegistrationOpen === false
                  ? bulandi2026Meta.mainRegistrationClosedLabel ||
                    "Registration closed"
                  : "Bulandi Registration",
                bulandi2026Meta.mainRegistrationOpen === false
                  ? null
                  : React.createElement(ChevronRight, {
                      className: "h-5 w-5",
                      "aria-hidden": true,
                      strokeWidth: 3,
                    }),
              ),
              React.createElement(
                bulandiRulebookPdfUrl?.trim() ? GhostCta : "span",
                bulandiRulebookPdfUrl?.trim()
                  ? {
                      as: "button",
                      type: "button",
                      onClick: () => setRulebookModalOpen(true),
                      className: "flex-1 min-w-[min(100%,14rem)]",
                    }
                  : {
                      className:
                        "flex min-h-[52px] flex-1 min-w-[min(100%,14rem)] items-center justify-center gap-2 rounded-xl border-2 border-dashed border-bul-border bg-bul-bg/60 px-6 py-4 font-mono text-sm font-bold uppercase tracking-wide text-bul-mutedfg",
                      role: "status",
                    },
                React.createElement(FileDown, {
                  className: `h-5 w-5 ${bulandiRulebookPdfUrl?.trim() ? "text-bul-lime" : "text-bul-mutedfg"}`,
                  "aria-hidden": true,
                  strokeWidth: 2.5,
                }),
                bulandiRulebookPdfUrl?.trim()
                  ? "Event Rulebook (PDF)"
                  : "Event Rulebook (PDF) \u2014 coming soon",
                bulandiRulebookPdfUrl?.trim()
                  ? React.createElement(ChevronRight, {
                      className: "h-5 w-5 text-bul-lime",
                      "aria-hidden": true,
                      strokeWidth: 2.75,
                    })
                  : null,
              ),
              bulandiSchedulePdfUrl?.trim()
                ? React.createElement(
                    GhostCta,
                    {
                      as: "button",
                      type: "button",
                      onClick: () => setScheduleModalOpen(true),
                      className: "flex-1 min-w-[min(100%,14rem)]",
                    },
                    React.createElement(CalendarDays, {
                      className: "h-5 w-5 text-bul-lime",
                      "aria-hidden": true,
                    }),
                    "View Schedule",
                    React.createElement(ChevronRight, {
                      className: "h-5 w-5 text-bul-lime",
                      "aria-hidden": true,
                      strokeWidth: 2.75,
                    }),
                  )
                : bulandi2026Meta.scheduleUrl
                ? React.createElement(
                    GhostCta,
                    {
                      as: "a",
                      href: bulandi2026Meta.scheduleUrl,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      className: "flex-1 min-w-[min(100%,14rem)]",
                    },
                    React.createElement(CalendarDays, {
                      className: "h-5 w-5 text-bul-lime",
                      "aria-hidden": true,
                    }),
                    "View Schedule",
                    React.createElement(ChevronRight, {
                      className: "h-5 w-5 text-bul-lime",
                      "aria-hidden": true,
                      strokeWidth: 2.75,
                    }),
                  )
                : React.createElement(
                    "span",
                    {
                      className:
                        "flex min-h-[52px] flex-1 min-w-[min(100%,14rem)] items-center justify-center gap-2 rounded-xl border-2 border-dashed border-bul-border bg-bul-bg/60 px-6 py-4 font-mono text-sm font-bold uppercase tracking-wide text-bul-mutedfg",
                      role: "status",
                    },
                    React.createElement(CalendarDays, {
                      className: "h-5 w-5 text-bul-mutedfg",
                      "aria-hidden": true,
                    }),
                    "Schedule \u2014 coming soon",
                  ),
            ),
            BULANDI_CONCLUDED
              ? React.createElement(
                  "p",
                  { className: "mt-6 text-sm text-bul-mutedfg leading-relaxed" },
                  "Head to the",
                  " ",
                  React.createElement(
                    "strong",
                    { className: "text-bul-lime" },
                    "Events",
                  ),
                  " tab for the full catalogue with rules and winners.",
                )
              : React.createElement(
              "p",
              { className: "mt-6 text-sm text-bul-mutedfg leading-relaxed" },
              "Already registered? Head to",
              " ",
              React.createElement(
                "strong",
                { className: "text-bul-lime" },
                "Event Sign In",
              ),
              " to pick or update your competition choices.",
            ),
          ),
        ),
      activeTab === "events" &&
        React.createElement(
          "section",
          {
            id: "events",
            role: "tabpanel",
            "aria-labelledby": "bulandi-tab-events",
            className: "scroll-mt-6 mb-12",
          },
          React.createElement(
            "header",
            { className: "mb-8 lg:mb-10 text-center sm:text-left" },
            React.createElement(
              "h2",
              { className: BUL_EVENTS_SECTION_HEADING_CLASS },
              React.createElement(
                "span",
                { className: "bul-heading-chrome" },
                "Bulandi 2026 \xB7 ",
              ),
              React.createElement(
                "span",
                { className: "bul-text-gradient-fire" },
                "Event Catalogue",
              ),
            ),
          ),
          React.createElement(
            "div",
            { className: "space-y-14" },
            React.createElement(
              "div",
              {
                id: "bulandi-events-juniors",
                className: bulandiEventsSectionWrapClass(
                  eventsHighlightTier === "juniors",
                ),
              },
              React.createElement(
                "div",
                {
                  className: "mb-4 flex flex-wrap items-end justify-between gap-3",
                },
                React.createElement(
                  "div",
                  null,
                  React.createElement(
                    "h3",
                    { className: BUL_EVENTS_SECTION_HEADING_CLASS },
                    React.createElement(
                      "span",
                      { className: "bul-heading-chrome" },
                      "Juniors ",
                    ),
                    React.createElement(
                      "span",
                      { className: "bul-text-gradient-fire" },
                      "4\u20138 years",
                    ),
                  ),
                  React.createElement(
                    "p",
                    {
                      className:
                        "mt-1 max-w-2xl font-mono text-xs uppercase tracking-wide text-bul-mutedfg",
                    },
                    "Born between 2 Jul 2017 and 1 Jul 2022 (inclusive).",
                  ),
                ),
                React.createElement(BulandiEventsRegisterCta, {
                  onRegister: handleEventCardRegister,
                }),
              ),
              React.createElement(
                "ul",
                {
                  className:
                    "grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3",
                },
                eventsJuniors.map((event) =>
                  React.createElement(EventCard, {
                    key: event.id,
                    event: event,
                    onViewRules: setRulesEvent,
                  }),
                ),
              ),
            ),
            React.createElement(
              "div",
              {
                id: "bulandi-events-intermediates",
                className: bulandiEventsSectionWrapClass(
                  eventsHighlightTier === "intermediates",
                ),
              },
              React.createElement(
                "div",
                {
                  className: "mb-4 flex flex-wrap items-end justify-between gap-3",
                },
                React.createElement(
                  "div",
                  null,
                  React.createElement(
                    "h3",
                    { className: BUL_EVENTS_SECTION_HEADING_CLASS },
                    React.createElement(
                      "span",
                      { className: "bul-heading-chrome" },
                      "Intermediates ",
                    ),
                    React.createElement(
                      "span",
                      { className: "bul-text-gradient-fire" },
                      "9\u201315 years",
                    ),
                  ),
                  React.createElement(
                    "p",
                    {
                      className:
                        "mt-1 max-w-2xl font-mono text-xs uppercase tracking-wide text-bul-mutedfg",
                    },
                    "Born between 2 Jul 2010 and 1 Jul 2017 (inclusive).",
                  ),
                ),
                React.createElement(BulandiEventsRegisterCta, {
                  onRegister: handleEventCardRegister,
                }),
              ),
              React.createElement(
                "ul",
                {
                  className:
                    "grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3",
                },
                eventsIntermediates.map((event) =>
                  React.createElement(EventCard, {
                    key: event.id,
                    event: event,
                    onViewRules: setRulesEvent,
                  }),
                ),
              ),
            ),
            React.createElement(
              "div",
              {
                id: "bulandi-events-seniors",
                className: bulandiEventsSectionWrapClass(
                  eventsHighlightTier === "seniors",
                ),
              },
              React.createElement(
                "div",
                {
                  className: "mb-4 flex flex-wrap items-end justify-between gap-3",
                },
                React.createElement(
                  "div",
                  null,
                  React.createElement(
                    "h3",
                    { className: BUL_EVENTS_SECTION_HEADING_CLASS },
                    React.createElement(
                      "span",
                      { className: "bul-heading-chrome" },
                      "Seniors ",
                    ),
                    React.createElement(
                      "span",
                      { className: "bul-text-gradient-fire" },
                      "16+",
                    ),
                  ),
                  React.createElement(
                    "p",
                    {
                      className:
                        "mt-1 max-w-2xl font-mono text-xs uppercase tracking-wide text-bul-mutedfg",
                    },
                    "Born on or before 1 Jul 2010.",
                  ),
                ),
                React.createElement(BulandiEventsRegisterCta, {
                  onRegister: handleEventCardRegister,
                }),
              ),
              React.createElement(
                "ul",
                {
                  className:
                    "grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3",
                },
                eventsSeniorsOnly.map((event) =>
                  React.createElement(EventCard, {
                    key: event.id,
                    event: event,
                    onViewRules: setRulesEvent,
                  }),
                ),
              ),
            ),
            React.createElement(
              "div",
              {
                id: "bulandi-events-family",
                className: bulandiEventsSectionWrapClass(
                  eventsHighlightTier === "family",
                ),
              },
              React.createElement(
                "div",
                {
                  className: "mb-4 flex flex-wrap items-end justify-between gap-3",
                },
                React.createElement(
                  "div",
                  null,
                  React.createElement(
                    "h3",
                    { className: BUL_EVENTS_SECTION_HEADING_CLASS },
                    React.createElement(
                      "span",
                      { className: "bul-heading-chrome" },
                      "Family ",
                    ),
                    React.createElement(
                      "span",
                      { className: "bul-text-gradient-fire" },
                      "event",
                    ),
                  ),
                  React.createElement(
                    "p",
                    {
                      className:
                        "mt-1 max-w-2xl font-mono text-xs uppercase tracking-wide text-bul-mutedfg",
                    },
                    "Team entry \u2014 see catalogue for member count and format.",
                  ),
                ),
                React.createElement(BulandiEventsRegisterCta, {
                  onRegister: handleEventCardRegister,
                }),
              ),
              React.createElement(
                "ul",
                {
                  className:
                    "grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3",
                },
                eventsFamilyOnly.map((event) =>
                  React.createElement(EventCard, {
                    key: event.id,
                    event: event,
                    onViewRules: setRulesEvent,
                  }),
                ),
              ),
            ),
          ),
        ),
      activeTab === "event-registration" &&
        React.createElement(
          "section",
          {
            id: "event-registration",
            role: "tabpanel",
            "aria-labelledby": "bulandi-tab-event-registration",
            className: "scroll-mt-6 mb-12",
          },
          React.createElement(SectionHeader, {
            title: "Event",
            accent: "Sign In",
            description: BULANDI_CONCLUDED
              ? "Bulandi 2026 has concluded and event sign-in is closed. Tap View rules or View winners on any event in the Events tab to revisit the season."
              : "Enter the B No. from your Bulandi confirmation email along with your date of birth, validate, then pick your competitions. Tap View rules on any event to preview and download its official rules sheet.",
            descriptionClassName: "!max-w-none w-full max-w-full",
          }),
          BULANDI_CONCLUDED
            ? React.createElement(BulandiClosedNotice, {
                className: "mb-8",
                body: "Event sign-in for Bulandi 2026 is closed. Head to the Events tab to browse the catalogue, rules and winners.",
              })
            : React.createElement(
            "div",
            {
              className:
                "mb-8 rounded-2xl border-2 border-bul-border bg-bul-surface/90 p-5 sm:p-6 shadow-bul-brutal-sm",
            },
            React.createElement(
              "h3",
              {
                className:
                  "mb-4 flex flex-wrap items-center gap-2 font-display text-[0.75rem] uppercase tracking-wide text-bul-fg",
              },
              React.createElement(BadgeCheck, {
                className: "h-5 w-5 shrink-0 text-bul-lime",
                "aria-hidden": true,
              }),
              "Your details",
            ),
            React.createElement(
              "div",
              {
                className: "mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4",
              },
              React.createElement(
                "div",
                null,
                React.createElement(
                  "label",
                  {
                    htmlFor: "bulandi-event-reg-br",
                    className:
                      "mb-1.5 block font-mono text-xs font-bold uppercase tracking-wider text-bul-mutedfg",
                  },
                  "B No. ",
                  React.createElement("span", { className: "text-bul-lime" }, "*"),
                ),
                React.createElement("input", {
                  id: "bulandi-event-reg-br",
                  type: "text",
                  autoComplete: "off",
                  placeholder: "e.g. B1511",
                  value: eventRegBr,
                  onChange: (e) => setEventRegBr(e.target.value),
                  className:
                    "w-full rounded-xl border-2 border-bul-border bg-bul-bg px-3 py-2.5 font-mono text-sm text-bul-fg placeholder:text-bul-mutedfg focus:border-bul-lime focus:outline-none focus:ring-2 focus:ring-bul-lime/25",
                }),
              ),
              React.createElement(
                "div",
                null,
                React.createElement(
                  "label",
                  {
                    htmlFor: "bulandi-event-reg-dob",
                    className:
                      "mb-1.5 block font-mono text-xs font-bold uppercase tracking-wider text-bul-mutedfg",
                  },
                  "Date of birth ",
                  React.createElement("span", { className: "text-bul-lime" }, "*"),
                ),
                React.createElement("input", {
                  id: "bulandi-event-reg-dob",
                  type: "date",
                  value: eventRegDob,
                  onChange: (e) => setEventRegDob(e.target.value),
                  className:
                    "w-full rounded-xl border-2 border-bul-border bg-bul-bg px-3 py-2.5 font-mono text-sm text-bul-fg focus:border-bul-lime focus:outline-none focus:ring-2 focus:ring-bul-lime/25",
                }),
              ),
            ),
            eventRegVerifyState === "ok" &&
              eventRegAgeBucket &&
              React.createElement(
                "p",
                {
                  className:
                    "mb-3 flex items-start gap-2 rounded-xl border border-bul-lime/35 bg-bul-lime/10 px-3 py-2 text-sm text-bul-fg",
                },
                React.createElement(BadgeCheck, {
                  className: "mt-0.5 h-4 w-4 shrink-0 text-bul-lime",
                  "aria-hidden": true,
                }),
                React.createElement(
                  "span",
                  null,
                  "Details validated. Competitions you already signed up for are pre-selected. Adjust your choices below, then submit.",
                ),
              ),
            React.createElement(
              PrimaryCta,
              {
                as: "button",
                type: "button",
                onClick: handleEventRegVerify,
                disabled: eventRegVerifyState === "loading",
                className: "min-h-[44px] py-2.5 text-sm",
              },
              eventRegVerifyState === "loading"
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(Loader2, {
                      className: "h-4 w-4 shrink-0 animate-spin",
                      "aria-hidden": true,
                    }),
                    "Validating\u2026",
                  )
                : React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(BadgeCheck, {
                      className: "h-4 w-4 shrink-0",
                      "aria-hidden": true,
                    }),
                    "Validate Details",
                  ),
            ),
          ),
          eventRegVerifyState === "ok" && eventRegAgeBucket
            ? React.createElement(
                "section",
                {
                  className:
                    "rounded-2xl border-2 border-bul-border bg-bul-surface/90 p-5 sm:p-6 shadow-bul-brutal-sm",
                },
                React.createElement(
                  "header",
                  { className: "mb-4 border-b border-bul-border pb-4" },
                  React.createElement(
                    "h3",
                    {
                      className:
                        "mb-2 flex flex-wrap items-center gap-2 font-display text-[0.75rem] uppercase tracking-wide text-bul-fg",
                    },
                    React.createElement(Trophy, {
                      className: `h-5 w-5 shrink-0 ${eventRegTierTrophyClass(eventRegAgeBucket)}`,
                      "aria-hidden": true,
                    }),
                    React.createElement(
                      "span",
                      null,
                      eventRegTierLabel(eventRegAgeBucket),
                    ),
                    React.createElement(
                      "span",
                      {
                        className: `rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${eventRegTierChipClass(eventRegAgeBucket)}`,
                      },
                      "Your competitions",
                    ),
                  ),
                  React.createElement(
                    "p",
                    { className: "text-sm leading-relaxed text-bul-mutedfg" },
                    eventRegTierBlurb(eventRegAgeBucket),
                  ),
                ),
                React.createElement(
                  React.Fragment,
                  null,
                  eventRegSoloList.length > 0
                    ? React.createElement(
                        React.Fragment,
                        null,
                        eventRegGroupList.length > 0
                          ? React.createElement(
                              "h4",
                              {
                                className:
                                  "mb-2 font-display text-[0.65rem] uppercase tracking-wide text-bul-mutedfg",
                              },
                              "Solo competitions",
                            )
                          : null,
                        React.createElement(BulandiSelectableEventList, {
                          events: eventRegSoloList,
                          eventRegSelectedIds: eventRegSelectedIds,
                          setEventRegSelectedIds: setEventRegSelectedIds,
                          eventRegFromSheetIds: eventRegFromSheetIds,
                          setEventRegSuccessModalOpen: setEventRegSuccessModalOpen,
                          onViewRules: setRulesEvent,
                          viralVoltageInstagramUrl: eventRegViralVoltageInstagramUrl,
                          setViralVoltageInstagramUrl: setEventRegViralVoltageInstagramUrl,
                          viralVoltageInstagramLocked: eventRegViralVoltageInstagramLocked,
                        }),
                      )
                    : null,
                  eventRegGroupList.length > 0
                    ? React.createElement(
                        React.Fragment,
                        null,
                        React.createElement(
                          "h4",
                          {
                            className: `mb-2 font-display text-[0.65rem] uppercase tracking-wide text-bul-mutedfg ${eventRegSoloList.length > 0 ? "mt-6" : ""}`,
                          },
                          "Group competitions",
                        ),
                        React.createElement(
                          "p",
                          {
                            className: "mb-3 text-xs leading-relaxed text-bul-mutedfg",
                          },
                          "Each group competition needs every teammate’s B No. and date of birth from the main Bulandi registration. All entries must match our records and be different from one another.",
                        ),
                        React.createElement(BulandiSelectableEventList, {
                          events: eventRegGroupList,
                          eventRegSelectedIds: eventRegSelectedIds,
                          setEventRegSelectedIds: setEventRegSelectedIds,
                          eventRegFromSheetIds: eventRegFromSheetIds,
                          setEventRegSuccessModalOpen: setEventRegSuccessModalOpen,
                          onViewRules: setRulesEvent,
                          registrationRows: eventRegSheetCacheRef.current.rows,
                          registrationHeaders: eventRegSheetCacheRef.current.headers,
                          groupTeammateByEventId: eventRegGroupTeammates,
                          setGroupTeammateByEventId: setEventRegGroupTeammates,
                          onClearGroupTeammatesForEvent: (evId, prevArr) => {
                            const prevBrs = (prevArr || [])
                              .map((t) =>
                                t && typeof t === "object"
                                  ? String(t.br ?? "").trim()
                                  : String(t ?? "").trim(),
                              )
                              .filter(Boolean);
                            if (prevBrs.length > 0) {
                              setEventRegGroupClearBrsByEventId((p) => ({
                                ...p,
                                [evId]: prevBrs,
                              }));
                            } else {
                              setEventRegGroupClearBrsByEventId((p) => {
                                const c = { ...p };
                                delete c[evId];
                                return c;
                              });
                            }
                          },
                        }),
                      )
                    : null,
                ),
                React.createElement(
                  "div",
                  { className: "mt-6 border-t border-bul-border pt-5" },
                  React.createElement(
                    PrimaryCta,
                    {
                      as: "button",
                      type: "button",
                      onClick: handleEventChoiceSubmit,
                      disabled: eventRegSubmitting,
                      className: "min-w-[200px] w-full sm:w-auto",
                    },
                    eventRegSubmitting
                      ? React.createElement(
                          React.Fragment,
                          null,
                          React.createElement(Loader2, {
                            className: "h-4 w-4 shrink-0 animate-spin",
                            "aria-hidden": true,
                          }),
                          "Saving\u2026",
                        )
                      : "Submit",
                  ),
                ),
              )
            : null,
        ),
      activeTab === "workshop-registration" &&
        React.createElement(BulandiWorkshopsTab, null),
      activeTab === "sponsors" &&
        React.createElement(
          "section",
          {
            id: "sponsors",
            role: "tabpanel",
            "aria-labelledby": "bulandi-tab-sponsors",
            className: "scroll-mt-6 mb-12",
          },
          React.createElement(
            "div",
            {
              className:
                "mb-3 flex flex-col items-center gap-3 sm:flex-row sm:justify-center",
            },
            React.createElement(Handshake, {
              className: "h-9 w-9 shrink-0 text-bul-lime",
              strokeWidth: 2,
              "aria-hidden": true,
            }),
            React.createElement(
              "h2",
              {
                className:
                  "font-display text-center text-[1.40625rem] uppercase tracking-tight text-bul-fg sm:text-[1.6875rem] lg:text-[2.25rem] bul-heading-chrome",
              },
              "Sponsors",
            ),
          ),
          React.createElement("p", {
            className:
              "mb-10 max-w-3xl mx-auto text-center text-sm leading-relaxed text-bul-mutedfg sm:text-base",
          }),
          React.createElement(
            "div",
            { className: "space-y-12 lg:space-y-14" },
            React.createElement(
              "div",
              null,
              React.createElement(
                "h3",
                {
                  className:
                    "mb-5 text-center font-mono text-xs font-bold uppercase tracking-[0.22em] text-bul-amber sm:text-sm md:text-base",
                },
                "Title sponsor",
              ),
              React.createElement(
                "div",
                { className: "flex justify-center" },
                React.createElement(SponsorPersonCard, {
                  entry: bulandiTitleSponsor,
                  size: "title",
                  bulandi: true,
                }),
              ),
            ),
            React.createElement(
              "div",
              null,
              React.createElement(
                "h3",
                {
                  className:
                    "mb-5 text-center font-mono text-xs font-bold uppercase tracking-[0.22em] text-bul-amber sm:text-sm md:text-base",
                },
                "Platinum sponsors",
              ),
              React.createElement(
                "ul",
                {
                  className:
                    "flex w-full max-w-5xl mx-auto flex-wrap justify-center items-stretch [--sp-gap:clamp(0.5rem,min(1.25rem,2.8vw),1.5rem)] gap-[var(--sp-gap)] px-[clamp(0.5rem,min(4vw,2rem),2.5rem)]",
                },
                bulandiPlatinumSponsors.map((entry, i) =>
                  React.createElement(
                    "li",
                    {
                      key: `bulandi-platinum-${i}`,
                      className:
                        "flex h-full min-h-0 min-w-0 shrink-0 grow-0 justify-center [flex-basis:calc((100%-var(--sp-gap))/2)] sm:[flex-basis:calc((100%-2*var(--sp-gap))/3)] lg:[flex-basis:calc((100%-4*var(--sp-gap))/5)]",
                    },
                    React.createElement(SponsorPersonCard, {
                      entry: entry,
                      size: "platinum",
                      bulandi: true,
                    }),
                  ),
                ),
              ),
            ),
            React.createElement(
              "div",
              null,
              React.createElement(
                "h3",
                {
                  className:
                    "mb-5 text-center font-mono text-xs font-bold uppercase tracking-[0.22em] text-bul-amber sm:text-sm md:text-base",
                },
                "Gold sponsors",
              ),
              React.createElement(
                "ul",
                {
                  className:
                    "flex w-full max-w-5xl mx-auto flex-wrap justify-center items-stretch [--sp-gap:clamp(0.375rem,min(1rem,2.2vw),1.25rem)] gap-[var(--sp-gap)] px-[clamp(0.5rem,min(3.5vw,1.75rem),2.25rem)]",
                },
                bulandiGoldSponsors.map((entry, i) =>
                  React.createElement(
                    "li",
                    {
                      key: `bulandi-gold-${i}`,
                      className:
                        "flex h-full min-h-0 min-w-0 shrink-0 grow-0 justify-center [flex-basis:calc((100%-var(--sp-gap))/2)] sm:[flex-basis:calc((100%-2*var(--sp-gap))/3)] lg:[flex-basis:calc((100%-4*var(--sp-gap))/5)]",
                    },
                    React.createElement(SponsorPersonCard, {
                      entry: entry,
                      size: "gold",
                      bulandi: true,
                    }),
                  ),
                ),
              ),
            ),
            React.createElement(
              "div",
              null,
              React.createElement(
                "h3",
                {
                  className:
                    "mb-5 text-center font-mono text-xs font-bold uppercase tracking-[0.22em] text-bul-amber sm:text-sm md:text-base",
                },
                "Mentor and Guide",
              ),
              React.createElement(
                "ul",
                {
                  className:
                    "flex w-full max-w-3xl mx-auto flex-wrap justify-center items-stretch [--sp-gap:clamp(0.5rem,min(1.25rem,2.8vw),1.5rem)] gap-[var(--sp-gap)] px-[clamp(0.5rem,min(4vw,2rem),2.5rem)]",
                },
                bulandiAdvisors.map((entry, i) =>
                  React.createElement(
                    "li",
                    {
                      key: `bulandi-advisor-${i}`,
                      className:
                        "flex h-full min-h-0 min-w-0 shrink-0 grow-0 justify-center [flex-basis:calc((100%-var(--sp-gap))/2)] sm:[flex-basis:calc((100%-2*var(--sp-gap))/3)]",
                    },
                    React.createElement(SponsorPersonCard, {
                      entry: entry,
                      size: "advisor",
                      bulandi: true,
                    }),
                  ),
                ),
              ),
            ),
          ),
        ),
      activeTab !== "workshop-registration" &&
        activeTab !== "sponsors" &&
        React.createElement(BulandiContactSection, null),
    ),
    eventRegSuccessModalOpen &&
      React.createElement(EventRegistrationSuccessModal, {
        onClose: () => setEventRegSuccessModalOpen(false),
      }),
    (eventRegSubmitError || eventRegError) &&
      React.createElement(EventRegistrationErrorModal, {
        message: eventRegSubmitError || eventRegError,
        onClose: () => {
          setEventRegSubmitError("");
          setEventRegError("");
        },
      }),
    React.createElement(BulandiRegistrationDrawer, {
      open:
        registrationDrawerOpen &&
        bulandi2026Meta.mainRegistrationOpen !== false,
      onClose: () => setRegistrationDrawerOpen(false),
      onRegistered: ({ brNumber, dob }) => {
        setRegistrationDrawerOpen(false);
        const brNum = parseBrNumeric(brNumber);
        const tier = eventRegistrationTierFromBrNumeric(brNum);
        const scrollTier =
          tier === "juniors" || tier === "intermediates" || tier === "seniors"
            ? tier
            : undefined;
        activateBulandiTab("events", scrollTier);
        setBrConfirm({ brNumber, dob });
      },
    }),
    brConfirm &&
      React.createElement(BrConfirmationModal, {
        brNumber: brConfirm.brNumber,
        onClose: () => setBrConfirm(null),
        onGoToEventRegistration:
          brConfirm.dob ? handleBrConfirmGoToEventRegistration : undefined,
      }),
    scheduleModalOpen &&
      React.createElement(BulandiScheduleModal, {
        pdfUrl: bulandiSchedulePdfUrl,
        downloadFilename: bulandiScheduleDownloadFilename,
        onClose: () => setScheduleModalOpen(false),
      }),
    rulebookModalOpen &&
      React.createElement(BulandiRulebookPdfModal, {
        pdfUrl: bulandiRulebookPdfUrl,
        downloadFilename: bulandiRulebookDownloadFilename,
        onClose: () => setRulebookModalOpen(false),
      }),
    rulesEvent &&
      React.createElement(RulesModal, {
        event: rulesEvent,
        onClose: () => setRulesEvent(null),
      }),
  );
};

export default Bulandi2026Page;
